import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from './Sidebar';
import ProfileMenu from './ProfileMenu';
import MobileHamburger from './MobileHamburger';
import MobileMenu from './MobileMenu';
import './Main.css';
import { supabase } from './supabaseClient';
import { GoogleMap, useJsApiLoader, InfoWindow, OverlayView } from '@react-google-maps/api';
import { FaBuilding, FaTent, FaHouseUser, FaMapPin } from 'react-icons/fa6';
import {FaRunning} from 'react-icons/fa';
import {} from "react-icons/tb";
import Preloader from './Preloader'; 
import './Main.css';

const libraries = ['places', 'marker'];

function EvacuationMap() {
  const [shelters, setShelters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mapCenter, setMapCenter] = useState({ lat: -6.2088, lng: 106.8456 }); 
  const [totalEvacuationCenters, setTotalEvacuationCenters] = useState(0);
  const [totalEvacuees, setTotalEvacuees] = useState(0);
  const [activeTemporaryShelters, setActiveTemporaryShelters] = useState(0);
  const [fullCapacityCenters, setFullCapacityCenters] = useState(0);
  const [selectedShelter, setSelectedShelter] = useState(null);
  const [tempShelters, setTempShelters] = useState([]);
  const [shelterFilter, setShelterFilter] = useState('all');
  const [sidebarNarrow, setSidebarNarrow] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: "AIzaSyAugo7vMiL-tP9dyz6mlCvcZqcovWJl1eA", 
    libraries
  });

  const mapContainerStyle = {
    width: '100%',
    height: '600px',
    borderRadius: '12px'
  };

  const mapOptions = useMemo(() => ({
    disableDefaultUI: true,
    zoomControl: true,
    mapId: 'YOUR_MAP_ID', // Replace with your actual Map ID
    styles: [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }]
      }
    ]
  }), []);

  useEffect(() => {
      document.title = "Evacuation Map";
    }, []);

  useEffect(() => {
    fetchShelters();
    fetchTempShelters();

    const sheltersChannel = supabase
      .channel('public:evacuation_shelters_map')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'evacuation_shelters' },
        (payload) => {
          console.log('Evacuation shelter change received!', payload);
          fetchShelters();
        }
      )
      .subscribe();

    const tempSheltersChannel = supabase
      .channel('public:temp_shelters_map')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'temp_shelter' },
        (payload) => {
          console.log('Temporary shelter change received!', payload);
          fetchTempShelters();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(sheltersChannel);
      supabase.removeChannel(tempSheltersChannel);
    };
  }, []);

  const filteredShelters = useMemo(() => {
    if (shelterFilter === 'all') {
      return shelters;
    } else if (shelterFilter === 'evacuation') {
      return shelters.filter(shelter => shelter.shelter_type === 'evacuation_center');
    } else if (shelterFilter === 'temporary') {
      return shelters.filter(shelter => shelter.shelter_type === 'temporary_shelter');
    }
    return shelters;
  }, [shelters, shelterFilter]);

  async function fetchShelters() {
    setLoading(true);
    setError('');
    
    let evacuationCenters = [];
    let temporaryShelters = [];

    // Fetch Evacuation Shelters
    const { data: evacuationData, error: evacuationError } = await supabase
      .from('evacuation_shelters')
      .select('*');

    if (evacuationError) {
      console.error('Error fetching evacuation shelters:', evacuationError);
      setError('Failed to load evacuation shelters');
    } else {
      evacuationCenters = evacuationData.map(shelter => ({
        ...shelter,
        shelter_type: 'evacuation_center',
        shelter_name: shelter.shelter_name || shelter.address,
        latitude: shelter.latitude,
        longitude: shelter.longitude,
      }));
    }

    // Fetch Temporary Shelters
    const { data: tempData, error: tempError } = await supabase
      .from('temp_shelter')
      .select('*')
      .eq('status', 'approved'); // Only show approved temporary shelters

    if (tempError) {
      console.error('Error fetching temporary shelters:', tempError);
      setError(prev => prev ? prev + ' and temporary shelters' : 'Failed to load temporary shelters');
    } else {
      temporaryShelters = tempData.map(shelter => ({
        ...shelter,
        shelter_type: 'temporary_shelter',
        shelter_name: shelter.house_name || shelter.address,
        latitude: shelter.latitude,
        longitude: shelter.longitude,
      }));
    }

    const allShelters = [...evacuationCenters, ...temporaryShelters].filter(shelter =>
      shelter.latitude && typeof shelter.latitude === 'number' &&
      shelter.longitude && typeof shelter.longitude === 'number'
    );

    setShelters(allShelters);

    // Calculate statistics
    let evacuationCentersCount = 0;
    let totalOccupancy = 0;
    let activeTempSheltersCount = 0;
    let fullSheltersCount = 0;

    // First count evacuation center occupants
    evacuationCenters.forEach(shelter => {
      evacuationCentersCount++;
      totalOccupancy += (shelter.accepted_occupants || 0);
      if ((shelter.accepted_occupants || 0) >= shelter.capacity) {
        fullSheltersCount++;
      }
    });

    // Then add temporary shelter occupants
    temporaryShelters.forEach(shelter => {
      activeTempSheltersCount++;
      totalOccupancy += (shelter.members || 0) + (shelter.accepted_occupants || 0);
      if (((shelter.members || 0) + (shelter.accepted_occupants || 0)) >= shelter.capacity) {
        fullSheltersCount++;
      }
    });

    setTotalEvacuationCenters(evacuationCentersCount);
    setTotalEvacuees(totalOccupancy);
    setActiveTemporaryShelters(activeTempSheltersCount);
    setFullCapacityCenters(fullSheltersCount);

    if (allShelters.length > 0) {
      // Set map center to the first shelter found
      setMapCenter({ 
        lat: allShelters[0].latitude,
        lng: allShelters[0].longitude
      });
    }
    
    setLoading(false);
  }

  async function fetchTempShelters() {
    try {
      const { data, error } = await supabase
        .from('temp_shelter')
        .select('*')
        .eq('status', 'approved'); // Only show approved temporary shelters

      if (error) {
        console.error('Error fetching temporary shelters:', error);
        setTempShelters([]);
      } else {
        setTempShelters(data || []);
      }
    } catch (error) {
      console.error('Error in fetchTempShelters:', error);
      setTempShelters([]);
    }
  }

  const getMarkerIcon = (shelterType) => {
    if (shelterType === 'evacuation_center') {
      return <FaBuilding/>;
    } else if (shelterType === 'temporary_shelter') {
      return <FaTent/>;
    }
    return <FaMapPin/>;
  };

  if (loading) return <Preloader text="Loading Map..." />;
    
  if (error) return <div style={{ color: 'red' }}>{error}</div>;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
      <Sidebar onNarrowChange={setSidebarNarrow}/>
      <div style={{ flex: 1, padding: 32 }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* Mobile Hamburger Menu */}
          <MobileHamburger
              isOpen={showMobileMenu}
              onToggle={() => setShowMobileMenu(prev => !prev)}
          />
          <div className="title-text">Evacuation Monitoring Map</div>
          </div>
          <ProfileMenu />
        </div>

        {/* Mobile Dropdown Menu */}
        <MobileMenu 
          isOpen={showMobileMenu} 
          onClose={() => setShowMobileMenu(false)} 
        />

        {/* Shelter Counts */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
          <div style={{ background: '#e3f2fd', borderRadius: 12, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 16, color: '#1976d2', marginBottom: 8 }}>Total Evacuation Centers</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 'bold', color: '#1976d2' }}>{totalEvacuationCenters}</div>
              <span style={{ fontSize: 28, color: '#1976d2' }}><FaBuilding /></span>
            </div>
          </div>
          <div style={{ background: '#e8f5e9', borderRadius: 12, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 16, color: '#388e3c', marginBottom: 8 }}>Total Evacuees</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 'bold', color: '#388e3c' }}>{totalEvacuees}</div>
              <span style={{ fontSize: 28, color: '#388e3c' }}><FaRunning /></span>
            </div>
          </div>
          <div style={{ background: '#fff3e0', borderRadius: 12, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 16, color: '#ff9800', marginBottom: 8 }}>Active Temporary Shelters</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 'bold', color: '#ff9800' }}>{activeTemporaryShelters}</div>
              <span style={{ fontSize: 28, color: '#ff9800' }}><FaTent /></span>
            </div>
          </div>
          <div style={{ background: '#ffebee', borderRadius: 12, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 16, color: '#d32f2f', marginBottom: 8 }}>Full Capacity Centers</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 'bold', color: '#d32f2f' }}>{fullCapacityCenters}</div>
              <span style={{ fontSize: 28, color: '#d32f2f' }}><FaHouseUser /></span>
            </div>
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <div style={{ position: 'relative' }}>
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={mapCenter}
                zoom={12}
                options={mapOptions}
              >
                {filteredShelters.map((shelter) => (
                  <OverlayView
                    key={shelter.id}
                    position={{
                      lat: shelter.latitude,
                      lng: shelter.longitude,
                    }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    getPixelPositionOffset={(width, height) => ({
                      x: -(width / 2),
                      y: -(height),
                    })}
                  >
                    <div
                      onClick={() => setSelectedShelter(shelter)}
                      style={{
                        background: shelter.shelter_type === 'evacuation_center' ? '#1976d2' : '#ff9800',
                        color: 'white',
                        padding: '8px 12px',
                        borderRadius: '20px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <div style={{ marginRight: '8px', display: 'flex', alignItems: 'center', fontSize: '18px' }}>
                        {getMarkerIcon(shelter.shelter_type)}
                      </div>
                      <span>{shelter.shelter_name}</span>
                    </div>
                  </OverlayView>
                ))}

                {selectedShelter && (
                  <InfoWindow
                    position={{
                      lat: selectedShelter.latitude,
                      lng: selectedShelter.longitude,
                    }}
                    onCloseClick={() => setSelectedShelter(null)}
                  >
                    <div style={{ padding: '10px' }}>
                      {/* Image on top */}
                      {(() => {
                        let img = null;
                        if (selectedShelter.shelter_type === 'evacuation_center') {
                          img = selectedShelter.shelter_image1 || selectedShelter.shelter_image2 || selectedShelter.shelter_image3 || selectedShelter.shelter_image4;
                        } else if (selectedShelter.shelter_type === 'temporary_shelter') {
                          img = selectedShelter.house_image1 || selectedShelter.house_image2 || selectedShelter.house_image3 || selectedShelter.house_image4;
                        }
                        return img ? (
                          <img src={img} alt={selectedShelter.shelter_name || selectedShelter.address} style={{ width: '100px', height: 'auto', marginBottom: '10px', borderRadius: 8, display: 'block', marginLeft: 'auto', marginRight: 'auto' }} />
                        ) : null;
                      })()}
                      <h3 style={{ marginBottom: '5px', marginTop: 0 }}>{selectedShelter.shelter_name || selectedShelter.address}</h3>
                      <p><strong>Status:</strong> {selectedShelter.status ? selectedShelter.status.charAt(0).toUpperCase() + selectedShelter.status.slice(1) : '-'}</p>
                      <p><strong>Total Occupants:</strong> {selectedShelter.shelter_type === 'temporary_shelter' ? ((selectedShelter.members || 0) + (selectedShelter.accepted_occupants || 0)) : (selectedShelter.accepted_occupants || 0)}</p>
                      <p><strong>Remaining Capacity:</strong> {selectedShelter.capacity - (selectedShelter.shelter_type === 'temporary_shelter' ? ((selectedShelter.members || 0) + (selectedShelter.accepted_occupants || 0)) : (selectedShelter.accepted_occupants || 0))}</p>
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            ) : (
              <div style={{ height: '600px', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#e0e0e0', borderRadius: 12 }}>
                <Preloader text="Loading Maps..." />
              </div>
            )}
            {/* Filter Dropdown */}
            <div style={{
              position: 'absolute',
              top: 20,
              right: 20,
              background: 'rgba(255,255,255,0.9)',
              padding: 12,
              borderRadius: 8,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              zIndex: 101
            }}>
              <select style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ccc' }} onChange={(e) => setShelterFilter(e.target.value)}>
                <option value="all">All Shelters</option>
                <option value="evacuation">Evacuation Centers</option>
                <option value="temporary">Temporary Shelters</option>
              </select>
            </div>
            {/* Legend */}
            <div style={{ 
              position: 'absolute', 
              top: 20, 
              left: 20, 
              background: 'rgba(255,255,255,0.9)', 
              padding: 12, 
              borderRadius: 8, 
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
              zIndex: 100
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Legend:</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}><FaBuilding style={{color: '#1976d2'}}/> Evacuation Center</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FaTent style={{color: '#ff9800'}}/>Temporary Shelter</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EvacuationMap;